import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Network } from 'lucide-react';

export const Preloader = ({ onComplete }) => {
  const [isVisible, setIsVisible] = useState(true);
  const [videoError, setVideoError] = useState(false);

  useEffect(() => {
    // Auto-hide after 4 seconds even if video doesn't end
    const timer = setTimeout(() => {
      setIsVisible(false);
      if (onComplete) onComplete();
    }, 4000);

    return () => clearTimeout(timer);
  }, [onComplete]);

  const handleVideoEnd = () => {
    setIsVisible(false);
    if (onComplete) onComplete();
  };

  const handleVideoError = () => {
    setVideoError(true);
    // If video fails, show fallback for 2 seconds
    setTimeout(() => {
      setIsVisible(false);
      if (onComplete) onComplete();
    }, 2000);
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          data-testid="preloader"
          initial={{ opacity: 1 }}
          exit={{ opacity: 0, y: -50 }}
          transition={{ duration: 0.8, ease: 'easeInOut' }}
          className="fixed inset-0 z-[100] bg-gradient-to-b from-[#3a2a1a] via-[#0a0f1e] to-[#080410] flex items-center justify-center"
        >
          {!videoError ? (
            <video
              data-testid="preloader-video"
              autoPlay
              muted
              playsInline
              onEnded={handleVideoEnd}
              onError={handleVideoError}
              className="absolute inset-0 w-full h-full object-cover"
            >
              <source src="/intro-video.mp4" type="video/mp4" />
            </video>
          ) : (
            <FallbackLoader />
          )}
          
          {/* Logo overlay */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.3, duration: 0.5 }}
            className="relative z-10 flex flex-col items-center"
          >
            <div className="flex items-center gap-3 mb-4">
              <Network className="w-12 h-12 text-[#4facfe]" />
              <span className="font-outfit text-4xl font-bold text-white tracking-tight">
                TECHNEXUS
              </span>
            </div>
            <motion.div
              animate={{ width: ['0%', '100%'] }}
              transition={{ duration: 3, ease: 'easeInOut' }}
              className="h-1 bg-gradient-to-r from-[#00f2fe] to-[#4facfe] rounded-full"
              style={{ width: '200px' }}
            />
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const FallbackLoader = () => (
  <div className="absolute inset-0 flex items-center justify-center">
    <div className="relative">
      {/* Animated circles */}
      {[...Array(3)].map((_, i) => (
        <motion.div
          key={i}
          className="absolute rounded-full border border-[#4facfe]/30"
          style={{
            width: 100 + i * 60,
            height: 100 + i * 60,
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
          }}
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.3, 0.6, 0.3],
          }}
          transition={{
            duration: 2,
            delay: i * 0.3,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        />
      ))}
    </div>
  </div>
);

export default Preloader;
